package us.laantech.common.util

/**
 * Parse RFC 4180 CSV text into records, lazily — nothing is parsed until the
 * sequence is iterated, and each record is produced on demand.
 *
 *  - Fields are separated by commas; records by newlines (`\n`, `\r\n`, `\r`).
 *  - A field wrapped in double quotes may contain commas and newlines, and a
 *    doubled `""` inside it decodes to a single literal `"`.
 *  - Surrounding quotes are stripped. Unquoted fields are trimmed; quoted
 *    fields are kept verbatim.
 *  - Fully blank records (a blank line, or a line of only commas) are skipped,
 *    so a trailing newline at end-of-file does not produce an empty record.
 */
fun parseCsvRecords(text: String): Sequence<List<String>> = sequence {
    val record = mutableListOf<String>()
    val field = StringBuilder()
    var inQuotes = false
    var quoted = false               // did the current field open with a quote?

    fun takeField(): String {
        val raw = field.toString()
        field.setLength(0)
        return (if (quoted) raw else raw.trim()).also { quoted = false }
    }

    var i = 0
    while (i < text.length) {
        val c = text[i]
        if (inQuotes) {
            when {
                c == '"' && i + 1 < text.length && text[i + 1] == '"' -> {
                    field.append('"'); i += 2; continue          // escaped quote
                }
                c == '"' -> { inQuotes = false; i++; continue }  // closing quote
                else -> { field.append(c); i++; continue }
            }
        }
        when (c) {
            '"' -> { inQuotes = true; quoted = true }
            ',' -> record.add(takeField())
            '\n', '\r' -> {
                record.add(takeField())
                if (record.any { it.isNotBlank() }) yield(record.toList())
                record.clear()
                if (c == '\r' && i + 1 < text.length && text[i + 1] == '\n') i++
            }
            else -> field.append(c)
        }
        i++
    }
    // Flush a final record that had no trailing newline.
    if (field.isNotEmpty() || quoted || record.isNotEmpty()) {
        record.add(takeField())
        if (record.any { it.isNotBlank() }) yield(record.toList())
    }
}
